import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { SearchArea } from '@/lib/areaSearch';
import { WARSAW_MSI_REJONY } from '@/data/warsawMsiRejony';

/**
 * Decide which outlines flat.area_boundaries should return for a SERP scope.
 * Pure + testable (see useAreaBoundaries.test.ts).
 *
 * The city outline is ALWAYS requested (unless the user picked a different city),
 * because it is the user's frame of reference: after zooming out to draw a custom
 * area you must be able to see where the city is. It used to be suppressed by ANY
 * narrower chip — that was a fix for the wrong layer. The outline joining the
 * fit UNION is what swallowed a tight target (e.g. a metro station), so the
 * exclusion belongs in the fit, not in the fetch: MapView's FitToPins drops
 * `properties.kind === 'city'` features from the union whenever a narrower intent
 * exists, and falls back to the city outline only when there is none.
 */
export function resolveBoundaryScope(areas: SearchArea[]) {
  const districts = areas.filter((a) => a.kind === 'district').map((a) => a.name);
  const subdistricts = areas.filter((a) => a.kind === 'subdistrict').map((a) => a.name);
  const hasOtherCity = areas.some((a) => a.kind === 'city');
  const includeCity = !hasOtherCity;
  const enabled = includeCity || districts.length > 0 || subdistricts.length > 0;
  return { districts, subdistricts, includeCity, enabled };
}

/**
 * Outline geometry for the selected SERP scope, drawn on the map so the user
 * sees the shape they're searching: a district chip → that district; a
 * subdistrict (rejon) chip → that rejon. The city outline comes along in every
 * response as context (see resolveBoundaryScope).
 *
 * One call to flat.area_boundaries returns a GeoJSON FeatureCollection (already
 * simplified server-side), each feature tagged with `properties.kind` ∈
 * city | district | subdistrict. Estate (geo / bbox) and station chips have no
 * rejon polygon, so they add nothing of their own — the city outline is what
 * keeps them locatable.
 */
export function useAreaBoundaries(areas: SearchArea[] = []) {
  const { districts, subdistricts, includeCity, enabled } = resolveBoundaryScope(areas);

  return useQuery<GeoJSON.FeatureCollection | null>({
    queryKey: [
      'flat.area_boundaries',
      [...districts].sort(),
      [...subdistricts].sort(),
      includeCity,
    ],
    enabled,
    staleTime: 60 * 60 * 1000, // boundaries are static reference data
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('flat' as never)
        .rpc('area_boundaries' as never, {
          p_districts: districts.length ? districts : null,
          p_subdistricts: subdistricts.length ? subdistricts : null,
          p_include_city: includeCity,
        } as never);
      if (error) throw error;
      return (data ?? null) as unknown as GeoJSON.FeatureCollection | null;
    },
  });
}

/**
 * Granice WSZYSTKICH osiedli (143 rejony MSI) — cele dotknięcia w trybie wyboru obszaru na
 * mapie (etap 3).
 *
 * DLACZEGO Z BAZY, A NIE Z PLIKU W REPO. Granice dzielnic jeżdżą u nas jako moduł statyczny
 * (`src/data/districtBoundaries.ts`, 514 kB), więc kuszące było zrobić tak samo z rejonami.
 * Zmierzone: jedno wywołanie `area_boundaries` ze 143 nazwami to 92,5 kB i 0,23 s, a geometria
 * wraca UPROSZCZONA po stronie bazy (Muranów: 10 wierzchołków). Plik w repo byłby więc drugą
 * kopią tych samych danych — z tą różnicą, że rozjeżdżałby się po każdej aktualizacji MSI, a
 * o aktualizacji nikt by nie pamiętał. Tu rozjazd jest niemożliwy z definicji.
 *
 * DLACZEGO NIE Z KAFELKÓW, skoro nazwy osiedli w nich są. Bo to dwa różne zbiory nazw:
 * rekonesans z 26.09 zmierzył 62% pokrycia MSI nazwami z kafelków i 184 nazwy z kafelków,
 * których w MSI nie ma (Jazdów, Dynasy, Latawiec). Dotknięcie „Latawca" dałoby zero ofert, bo
 * po tej nazwie nasz filtr nie znajdzie niczego. Typografię bierzemy z kafelków za darmo,
 * granice i CELE DOTKNIĘCIA muszą być nasze.
 *
 * Pobieramy dopiero przy wejściu w tryb (`enabled`), raz na sesję.
 */
/**
 * Granice WSZYSTKICH dzielnic (18) — cele dotknięcia w trybie wyboru obszaru.
 *
 * DLACZEGO Z BAZY, A NIE Z `src/data/districtBoundaries.ts`. Ten moduł ma 514 kB i jeździł
 * osobną paczką dociąganą DOPIERO przy wejściu w tryb. Na telefonie w sieci komórkowej znaczy
 * to kilka sekund, w których tryb jest włączony, pasek mówi „Kliknij dzielnice", a na mapie
 * nie ma czego kliknąć — dokładnie to zobaczył Piotr 27.09 i zgłosił jako „tryb nie rysuje
 * dzielnic". To samo wywołanie co dla osiedli daje geometrię UPROSZCZONĄ po stronie bazy:
 * zmierzone 39,9 kB i 0,31 s na miasto plus 18 dzielnic, czyli trzynaście razy mniej.
 *
 * Moduł statyczny zostaje tam, gdzie był potrzebny od początku — w wyborze dzielnic przy
 * zakładaniu konta (`CitySuburbMap`), który nie ma z tą mapą nic wspólnego.
 */
export function useWszystkieDzielnice(enabled: boolean) {
  return useQuery<GeoJSON.Feature[]>({
    queryKey: ['flat.area_boundaries', 'wszystkie-dzielnice'],
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('flat' as never)
        .rpc('area_boundaries' as never, {
          p_districts: WARSZAWSKIE_DZIELNICE,
          p_subdistricts: null,
          p_include_city: false,
        } as never);
      if (error) throw error;
      return ((data as unknown as GeoJSON.FeatureCollection | null)?.features ?? []) as GeoJSON.Feature[];
    },
  });
}

/** 18 dzielnic Warszawy — z tego samego słownika MSI, z którego bierze je wyszukiwarka. */
const WARSZAWSKIE_DZIELNICE = [...new Set(WARSAW_MSI_REJONY.map((r) => r.district))].sort();

export function useWszystkieRejony(enabled: boolean) {
  return useQuery<GeoJSON.Feature[]>({
    queryKey: ['flat.area_boundaries', 'wszystkie-rejony'],
    enabled,
    staleTime: 24 * 60 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .schema('flat' as never)
        .rpc('area_boundaries' as never, {
          p_districts: null,
          p_subdistricts: WARSAW_MSI_REJONY.map((r) => r.name),
          p_include_city: false,
        } as never);
      if (error) throw error;
      return ((data as unknown as GeoJSON.FeatureCollection | null)?.features ?? []) as GeoJSON.Feature[];
    },
  });
}
